import { memo, useContext, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { createOfferStructuredData, SCHEMA_TYPES } from 'components/utils/OfferStructuredData'
import CurrencyContext from 'contexts/currencyContext'
import GeoContext from 'contexts/geoContext'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import useMetasearchDiscount from 'hooks/useMetasearchDiscount'
import { useGhaVerifierStructuredPrice } from 'hooks/useGhaVerifierPrice'
import { useAppSelector } from 'hooks/reduxHooks'
import {
  getPropertyCheapestHotelOnlyRateForDiscount,
  getPropertyHotelOnlyRates,
} from 'selectors/offerPage/offerPageSelectors'

interface Props {
  offer: App.HotelOffer | App.BedbankOffer
}

/**
 * `Hotel` JSON-LD for the SPP (bedbank and LPC offers alike), read by the Google Hotel Ads crawler
 * to verify on-page prices. Pricing comes from the directly listed hotel-only property rates, with
 * the metasearch discount applied to the cheapest/prominent rate so the emitted price matches the
 * price shown on the page.
 *
 * The schema is Google's required minimum — `name`, `identifier`, `makesOffer`
 * (https://developers.google.com/hotels/hotel-prices/structured-data/hotel-price-structured-data):
 * - `address` is optional per the spec, but all of its sub-fields (including `postalCode`) become
 *   mandatory once it's present, and accommodation service exposes no postal code — so the block is
 *   omitted rather than emitted incomplete.
 * - `telephone` and `description` aren't part of the spec, so the crawler ignores them.
 * - `identifier` must be the hotel ID in the GHA feed, which is the bedbank ID: GHA entry links
 *   resolve through `offerPageURL`, which routes bedbank offers to `/partner/...` exclusively, so
 *   `offer.id` is that bedbank ID on every page the crawler verifies. `/offer/...` pages emit the
 *   LPC offer ID instead, which the crawler never visits.
 */
function PropertySppStructuredData(props: Props) {
  const { offer } = props
  const utmBp = useAppSelector(state => state.utm.bp)
  const geo = useContext(GeoContext)
  const contextCurrency = useContext(CurrencyContext)
  const offerPageState = useContext(OfferPageStateContext)
  // GHA SSR seeds this; otherwise PropertyContainer useEffect.
  const property = offerPageState.property
  // The state dates, not the URL query: the rates below are fetched with a key built from these
  // (see `getPropertyRatesKeyParams`), so the emitted prices always belong to these dates. The URL
  // query only seeds them (after validation) and is rewritten from state when they change.
  const { checkInDate, checkOutDate } = offerPageState

  const hotelOnlyRates = useAppSelector(appState => getPropertyHotelOnlyRates(offerPageState, appState))
  const discountRate = useAppSelector(appState => getPropertyCheapestHotelOnlyRateForDiscount(offerPageState, appState))

  // Look up the discount already fetched for the cheapest bedbank hotel-only rate. The request key
  // includes the price, so we pass the same `displayPrice` the dispatch used to resolve it.
  const [discountAmount] = useMetasearchDiscount(discountRate?.totals.displayPrice ?? 0, {
    propertyId: discountRate?.service.type === 'bedbank' ? discountRate.service.propertyId : undefined,
    checkIn: checkInDate,
    checkOut: checkOutDate,
    rooms: offerPageState.rooms,
    disabled: !discountRate,
  })

  const currency = contextCurrency ?? geo.currentCurrency

  const ghaVerifierPrice = useGhaVerifierStructuredPrice()

  const pricingData = useMemo(() => {
    if (utmBp !== '3' || !checkInDate || !checkOutDate) {
      return {}
    }

    if (!hotelOnlyRates.length) {
      return {}
    }

    // Every directly listed hotel-only rate, undiscounted.
    const allOffers = hotelOnlyRates.map(rate => createOfferStructuredData(checkInDate, checkOutDate, {
      propertyFees: rate.totals.propertyFees,
      offerPrice: rate.totals.displayPrice,
      offerPriceBeforeTax: rate.totals.beforeTaxesFeesPrice,
      taxesAndFees: rate.totals.taxesAndFees,
    }, currency))

    if (!discountRate) {
      return { makesOffer: allOffers }
    }

    // Prepend the cheapest rate as the prominent offer GHA verifies — Google's own figures for the
    // verifier, otherwise the same formula as the SPP rate tiles (e.g. PropertyRateOption):
    // Math.max(displayPrice - discountAmount, 0).
    return {
      makesOffer: [createOfferStructuredData(checkInDate, checkOutDate, ghaVerifierPrice ?? {
        propertyFees: discountRate.totals.propertyFees,
        offerPrice: Math.max(discountRate.totals.displayPrice - discountAmount, 0),
        offerPriceBeforeTax: discountRate.totals.beforeTaxesFeesPrice,
        taxesAndFees: discountRate.totals.taxesAndFees,
      }, currency),
      ...allOffers,
      ],
    }
  }, [utmBp, checkInDate, checkOutDate, hotelOnlyRates, discountRate, discountAmount, currency, ghaVerifierPrice])

  const structuredData = useMemo(() => {
    if (!property) {
      return undefined
    }
    return {
      '@context': 'https://schema.org',
      '@type': SCHEMA_TYPES.HOTEL,
      name: property.name,
      identifier: offer.id,
      ...pricingData,
    }
  }, [property, offer.id, pricingData])

  if (!structuredData) {
    return null
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  )
}

export default memo(PropertySppStructuredData)
